/**
 * Public FAQ page (ask-a-question-faq.md §5.3 / feature ledger §2) — browse published Q&A by
 * topic (featured/pinned first), a keyword search box, and a "Was this helpful?" yes/no vote per
 * answer. No auth, no PII (the FAQ entity is structurally scrubbed server-side, NFR-3). Deep-links
 * to a single entry via `/faq/:id` render just that card (used by AskPage's related-FAQ typeahead
 * links) — `/faq` alone renders the full browse+search surface.
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { browseFAQ, searchFAQ, getFAQ, voteFAQ, ApiError, type FAQView } from "../api/index.js";
import { TownHeader } from "../components/TownHeader.js";
import { ResidentThemeRoot } from "../components/ResidentThemeRoot.js";
import { Field } from "../components/Field.js";
import { Button } from "../components/Button.js";
import { SearchIcon, CheckIcon } from "../components/icons.js";
import { Skeleton, SkeletonRegion } from "../components/Skeleton.js";
import { useTheme } from "../theme/ThemeProvider.js";
import { useDocumentTitle, pageTitle } from "../components/useDocumentTitle.js";
import { useEmbedClose } from "../components/useEmbedClose.js";
import { useEmbedResize } from "../components/useEmbedResize.js";

/** Sanitize-trusting render: answerHtml is server-sanitized (allowlist links/lists/emphasis, no
 *  script/style/on*) before it ever reaches this client (NFR-3) — safe to dangerouslySetInnerHTML
 *  the same way a CMS renders trusted rich text; the client never sanitizes on its own. */
function AnswerHtml({ html }: { html: string }): JSX.Element {
  // eslint-disable-next-line react/no-danger
  return <div className="td-faq-answer" dangerouslySetInnerHTML={{ __html: html }} />;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

interface FaqPageProps {
  embed?: boolean;
  chromeless?: boolean;
}

export function FaqPage({ embed = false, chromeless = false }: FaqPageProps): JSX.Element {
  const { id } = useParams<{ id: string }>();
  // chromeless only means anything inside an embed (mirrors MeetingsPage) — an inline FAQ embed
  // (data-td-display="inline" + data-td-title="") drops our own H1/skip-link + paper background so
  // it sits flush inside a host page that already has its own "FAQ" heading (the chrome-duplication
  // the meetings/code embeds already solve). Plain ?embed=1 keeps today's H1 (modal launcher chrome).
  if (id) return <FaqEntryPage embed={embed} chromeless={embed && chromeless} id={id} />;
  return <FaqBrowsePage embed={embed} chromeless={embed && chromeless} />;
}

// Makes the framed document's own <html>/<body> transparent while a chromeless surface is mounted
// (the .td-intake--chromeless container is transparent, but the iframe body still paints the cream
// --td-paper otherwise) — identical to MeetingsPage's effect; reverted on unmount.
function useChromelessBody(chromeless: boolean): void {
  useEffect(() => {
    if (!chromeless) return;
    const { documentElement: html, body } = document;
    html.classList.add("td-embed-transparent");
    body.classList.add("td-embed-transparent");
    return () => {
      html.classList.remove("td-embed-transparent");
      body.classList.remove("td-embed-transparent");
    };
  }, [chromeless]);
}

function FaqBrowsePage({
  embed,
  chromeless,
}: {
  embed: boolean;
  chromeless: boolean;
}): JSX.Element {
  const { config, questionTopics } = useTheme();
  useDocumentTitle(pageTitle("FAQ", config.townName));
  useEmbedClose(embed);
  useChromelessBody(chromeless);
  // Inline embed (apps/widget mountFaqInline) auto-sizes its iframe to this root's height — posts
  // `towndesk:faq-resize` on mount and whenever content height changes (e.g. a card accordion
  // expands/collapses). No-op on the standalone page and in the modal embed (nothing listens).
  const rootRef = useRef<HTMLDivElement>(null);
  useEmbedResize(embed, rootRef, "towndesk:faq-resize");
  const [params, setParams] = useSearchParams();
  const topic = params.get("topic") ?? "";
  const [searchInput, setSearchInput] = useState(params.get("q") ?? "");
  const debouncedQuery = useDebounced(searchInput, 300);

  const [faqs, setFaqs] = useState<FAQView[] | null>(null);
  const searchTitleId = useId();

  useEffect(() => {
    const next = new URLSearchParams(params);
    if (debouncedQuery) next.set("q", debouncedQuery);
    else next.delete("q");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery]);

  useEffect(() => {
    setFaqs(null);
    const q = debouncedQuery.trim();
    const load = q ? searchFAQ(q, topic || undefined) : browseFAQ(topic || undefined);
    load.then(setFaqs).catch(() => setFaqs([]));
  }, [debouncedQuery, topic]);

  const setTopic = (t: string) => {
    const next = new URLSearchParams(params);
    if (t) next.set("topic", t);
    else next.delete("topic");
    setParams(next, { replace: true });
  };

  return (
    <ResidentThemeRoot
      ref={rootRef}
      className={`td-intake${embed ? " td-intake--embed" : ""}${chromeless ? " td-intake--chromeless" : ""}`}
    >
      {chromeless ? null : (
        <a href="#td-faq-main" className="td-skip-link">
          Skip to the FAQ
        </a>
      )}
      <TownHeader embed={embed} subcopy={`Answers from ${config.townName}.`} />

      <main id="td-faq-main" className="td-intake__main td-faq">
        {chromeless ? null : <h1 className="td-intake__title">Frequently Asked Questions</h1>}

        <div className="td-faq__controls">
          <Field id="td-faq-search" label="Search the FAQ">
            {(aria) => (
              <div className="td-faq__searchbox">
                <SearchIcon width={18} height={18} aria-hidden="true" />
                <input
                  {...aria}
                  className="td-input"
                  type="search"
                  placeholder="Search questions and answers…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  aria-describedby={searchTitleId}
                />
              </div>
            )}
          </Field>
          <span id={searchTitleId} className="td-sr-only">
            Results update as you type
          </span>

          <label>
            <span className="td-sr-only">Filter by topic</span>
            <select className="td-select" value={topic} onChange={(e) => setTopic(e.target.value)}>
              <option value="">All topics</option>
              {questionTopics.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        {faqs === null ? (
          <FaqSkeleton />
        ) : faqs.length === 0 ? (
          <p className="td-faq__empty">
            {debouncedQuery ? "No FAQs match your search." : "No published FAQs yet."}
          </p>
        ) : (
          <ul className="td-faq__list" aria-live="polite">
            {faqs.map((f) => (
              <li key={f.id}>
                <FaqCard faq={f} />
              </li>
            ))}
          </ul>
        )}

        <p className="td-faq__cta">
          Didn't find your answer? <a href="/ask">Ask a question</a>.
        </p>
      </main>

      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
      </footer>
    </ResidentThemeRoot>
  );
}

function FaqEntryPage({
  embed,
  chromeless,
  id,
}: {
  embed: boolean;
  chromeless: boolean;
  id: string;
}): JSX.Element {
  const { config } = useTheme();
  const navigate = useNavigate();
  useChromelessBody(chromeless);
  const [state, setState] = useState<"loading" | "found" | "notfound">("loading");
  const [faq, setFaq] = useState<FAQView | null>(null);
  useDocumentTitle(pageTitle(faq?.question ?? "FAQ", config.townName));

  useEffect(() => {
    setState("loading");
    getFAQ(id).then((f) => {
      setFaq(f);
      setState(f ? "found" : "notfound");
    });
  }, [id]);

  return (
    <ResidentThemeRoot
      className={`td-intake${embed ? " td-intake--embed" : ""}${chromeless ? " td-intake--chromeless" : ""}`}
    >
      <TownHeader embed={embed} subcopy={`Answers from ${config.townName}.`} />
      <main className="td-intake__main td-faq">
        {state === "loading" ? (
          <FaqSkeleton />
        ) : state === "notfound" ? (
          <>
            <h1 className="td-intake__title">FAQ not found</h1>
            <p className="td-track__sub">
              This answer isn't published, or the link is out of date.
            </p>
            <Button onClick={() => navigate("/faq")}>Browse the FAQ</Button>
          </>
        ) : faq ? (
          <>
            <Button variant="ghost" onClick={() => navigate("/faq")}>
              ← All FAQs
            </Button>
            <FaqCard faq={faq} expanded />
          </>
        ) : null}
      </main>
      <footer className="td-footer">
        <span>
          Powered by{" "}
          <a href="https://towndesk.us" target="_blank" rel="noopener noreferrer">
            TownDesk
          </a>
        </span>
      </footer>
    </ResidentThemeRoot>
  );
}

function FaqCard({ faq, expanded = false }: { faq: FAQView; expanded?: boolean }): JSX.Element {
  const [open, setOpen] = useState(expanded);
  const bodyId = useId();

  return (
    <article className={`td-faq-card${faq.featured ? " td-faq-card--featured" : ""}`}>
      {faq.featured ? <span className="td-faq-card__badge">Featured</span> : null}
      {expanded ? (
        <h2 className="td-faq-card__q">{faq.question}</h2>
      ) : (
        <button
          type="button"
          className="td-faq-card__toggle"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((o) => !o)}
        >
          <span className="td-faq-card__q">{faq.question}</span>
          <span className="td-faq-card__toggle-icon" aria-hidden="true">
            {open ? "–" : "+"}
          </span>
        </button>
      )}
      <div id={bodyId} hidden={!open}>
        <AnswerHtml html={faq.answerHtml} />
        <FaqVote faqId={faq.id} initialYes={faq.helpfulYes} initialNo={faq.helpfulNo} />
      </div>
    </article>
  );
}

function FaqVote({
  faqId,
  initialYes,
  initialNo,
}: {
  faqId: string;
  initialYes: number;
  initialNo: number;
}): JSX.Element {
  const [voted, setVoted] = useState<"yes" | "no" | null>(null);
  const [counts, setCounts] = useState({ yes: initialYes, no: initialNo });
  const [error, setError] = useState<string | null>(null);

  const vote = useCallback(
    async (helpful: boolean) => {
      if (voted) return; // one vote per view (client-side guard; §5.3 anti-abuse note)
      setError(null);
      const next: "yes" | "no" = helpful ? "yes" : "no";
      setVoted(next);
      setCounts((c) => ({ ...c, [next]: c[next] + 1 }));
      try {
        await voteFAQ(faqId, helpful);
      } catch (err) {
        // Roll back optimistic count on failure, but let the user try again.
        setCounts((c) => ({ ...c, [next]: c[next] - 1 }));
        setVoted(null);
        setError(err instanceof ApiError ? err.message : "Couldn't record your vote — try again.");
      }
    },
    [faqId, voted],
  );

  return (
    <div className="td-faq-vote" role="group" aria-label="Was this helpful?">
      <span className="td-faq-vote__prompt">Was this helpful?</span>
      <button
        type="button"
        className={`td-faq-vote__btn${voted === "yes" ? " is-voted" : ""}`}
        onClick={() => vote(true)}
        disabled={Boolean(voted)}
        aria-pressed={voted === "yes"}
      >
        {voted === "yes" ? <CheckIcon width={14} height={14} aria-hidden="true" /> : null}
        Yes ({counts.yes})
      </button>
      <button
        type="button"
        className={`td-faq-vote__btn${voted === "no" ? " is-voted" : ""}`}
        onClick={() => vote(false)}
        disabled={Boolean(voted)}
        aria-pressed={voted === "no"}
      >
        No ({counts.no})
      </button>
      {voted ? (
        <span className="td-sr-only" role="status">
          Thanks for the feedback.
        </span>
      ) : null}
      {error ? (
        <span className="td-faq-vote__error" role="alert">
          {error}
        </span>
      ) : null}
    </div>
  );
}

function FaqSkeleton(): JSX.Element {
  return (
    <SkeletonRegion label="Loading FAQs…" className="td-skel-rows">
      {Array.from({ length: 4 }).map((_, i) => (
        <div className="td-skel-row" key={i}>
          <div className="td-skel-row__main">
            <Skeleton width="70%" />
            <Skeleton width="40%" height="var(--td-text-sm)" />
          </div>
        </div>
      ))}
    </SkeletonRegion>
  );
}
